import { useEffect, useRef, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import api from "../lib/api";
import { toast } from "sonner";

const STATUS_BADGE = {
  active: "bg-[#DCFCE7] text-[#166534]",
  inactive: "bg-paper text-quiet",
  notice: "bg-[#FEF3C7] text-[#92400E]",
  exited: "bg-[#FEE2E2] text-[#991B1B]",
};

const MAX_MB = 5;
const DOC_MAX_MB = {
  internship_letter: 5,
  offer_letter: 5,
  appointment_letter: 20,
};
const fileToB64 = (file) =>
  new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onerror = () => reject(new Error("Read error"));
    r.onload = () => resolve(String(r.result || "").split(",")[1] || "");
    r.readAsDataURL(file);
  });

export default function EmployeeDetail() {
  const { employeeDbId } = useParams();
  const nav = useNavigate();
  const [emp, setEmp] = useState(null);
  const [err, setErr] = useState("");

  const reload = async () => {
    const r = await api.get(`/employees/${employeeDbId}`);
    setEmp(r.data);
  };

  useEffect(() => {
    (async () => {
      try { await reload(); }
      catch (e) { setErr(e?.response?.data?.detail || "Failed to load employee"); }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [employeeDbId]);

  const download = async (doc) => {
    try {
      const r = await api.get(`/employees/${employeeDbId}/documents/${doc}`);
      const { filename, b64 } = r.data;
      const bin = atob(b64);
      const arr = new Uint8Array(bin.length);
      for (let i = 0; i < bin.length; i++) arr[i] = bin.charCodeAt(i);
      const blob = new Blob([arr]);
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = filename; document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to download");
    }
  };

  const uploadDoc = async (doc, file) => {
    const cap = DOC_MAX_MB[doc] ?? MAX_MB;
    if (file.size > cap * 1024 * 1024) {
      toast.error(`${file.name} exceeds ${cap} MB`);
      return;
    }
    try {
      const b64 = await fileToB64(file);
      await api.put(`/employees/${employeeDbId}/documents/${doc}`, { filename: file.name, b64 });
      toast.success(`${doc.replace(/_/g, " ")} uploaded`);
      await reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to upload");
    }
  };

  const removeDoc = async (doc) => {
    if (!window.confirm("Remove this document?")) return;
    try {
      await api.delete(`/employees/${employeeDbId}/documents/${doc}`);
      toast.success("Removed");
      await reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to remove");
    }
  };

  if (err) return <div className="p-8 text-error" data-testid="employee-detail-error">{err}</div>;
  if (!emp) return <div className="p-8 text-quiet">Loading…</div>;

  return (
    <div className="p-8 max-w-[1200px] mx-auto" data-testid="employee-detail-page">
      <Link to="/employees" className="text-xs text-quiet hover:text-ink font-mono">← Employees</Link>

      <div className="mt-4 border-b border-rule pb-6 flex items-start justify-between gap-6">
        <div className="min-w-0">
          <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-quiet">
            {emp.employee_id} · {emp.department || "—"}
          </div>
          <h1 className="font-serif text-5xl text-ink mt-2 leading-none">{emp.employee_name}</h1>
          <div className="mt-3 flex flex-wrap gap-2 items-center">
            <span className={`px-2 py-1 text-[10px] font-mono uppercase tracking-wider ${STATUS_BADGE[emp.employment_status] || "bg-paper text-quiet"}`} data-testid="emp-status-badge">
              {emp.employment_status}
            </span>
            <span className="px-2 py-1 border border-rule text-[10px] font-mono uppercase tracking-wider text-quiet bg-white">
              Tenure · {emp.tenure_days != null ? `${emp.tenure_days} d` : "—"}
            </span>
            {emp.employee_age != null && (
              <span className="px-2 py-1 border border-rule text-[10px] font-mono uppercase tracking-wider text-quiet bg-white">
                Age · {emp.employee_age}
              </span>
            )}
            {emp.bgv_status && (
              <span className={`px-2 py-1 text-[10px] font-mono uppercase tracking-wider ${
                emp.bgv_status === "green" ? "bg-[#DCFCE7] text-[#166534]" :
                emp.bgv_status === "amber" ? "bg-[#FEF3C7] text-[#92400E]" : "bg-[#FEE2E2] text-[#991B1B]"
              }`}>BGV · {emp.bgv_status}</span>
            )}
          </div>
        </div>
        <div className="flex flex-col gap-2 items-end shrink-0">
          <button
            data-testid="edit-onboarding-btn"
            onClick={() => nav(`/employees/onboarding/${emp.id}`)}
            className="text-xs font-mono uppercase tracking-wider border border-ink hover:bg-paper px-3 py-1.5"
          >
            Edit onboarding
          </button>
          <button
            data-testid="manage-exit-btn"
            onClick={() => nav(`/employees/${emp.id}/exit`)}
            className={`text-xs font-mono uppercase tracking-wider px-3 py-1.5 ${
              emp.employment_status === "exited"
                ? "border border-rule text-quiet hover:bg-paper"
                : emp.employment_status === "active"
                  ? "border border-error text-error hover:bg-error hover:text-white"
                  : "border border-error text-white bg-error hover:opacity-90"
            }`}
          >
            {emp.employment_status === "exited" ? "View exit" :
             emp.employment_status === "active" ? "Start exit" : "Manage exit →"}
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-6">
        <Card title="Personal">
          <KV k="Full name" v={emp.employee_name} />
          <KV k="Designation" v={emp.designation} />
          <KV k="Department" v={emp.department} />
          <KV k="Reporting manager" v={emp.reporting_manager} />
          <KV k="Date of joining" v={emp.date_of_joining} />
          <KV k="Gender" v={emp.gender} />
          <KV k="Date of birth" v={emp.date_of_birth} />
          <KV k="Blood group" v={emp.blood_group} />
          <KV k="Food preference" v={emp.food_preference === "veg" ? "Vegetarian" : emp.food_preference === "non_veg" ? "Non-Vegetarian" : "—"} />
          <KV k="Marital status" v={emp.marital_status} />
          <KV k="Spouse" v={emp.spouse_name} />
          <KV k="Father's name" v={emp.fathers_name} />
          <KV k="Mother's name" v={emp.mothers_name} />
        </Card>

        <Card title="Contact">
          <KV k="Personal contact 1" v={emp.personal_contact_1} />
          <KV k="Personal contact 2" v={emp.personal_contact_2} />
          <KV k="Official email" v={emp.official_email} />
          <KV k="Personal email" v={emp.personal_email} />
          <KV k="Current address" v={emp.current_address} pre />
          <KV k="Permanent address" v={emp.permanent_address} pre />
          <KV k="Highest qualification" v={emp.highest_qualification} />
          <KV k="Last company" v={emp.last_company} />
        </Card>

        <Card title="Emergency contact">
          <KV k="Name" v={emp.emergency_name} />
          <KV k="Number" v={emp.emergency_number} />
          <KV k="Relation" v={emp.emergency_relation} />
        </Card>

        <Card title="Government IDs · Bank" hint="Decrypted for HR view">
          <KV k="PAN" v={emp.pan_number} mono />
          <KV k="Aadhaar" v={emp.aadhaar} mono />
          <KV k="UAN" v={emp.uan} mono />
          <div className="border-t border-rule my-2" />
          <KV k="Bank" v={emp.bank_name} />
          <KV k="Account number" v={emp.bank_account_number} mono />
          <KV k="IFSC" v={emp.ifsc_code} mono />
          <KV k="Branch" v={emp.bank_branch} />
        </Card>

        <Card title="BGV">
          <KV k="Status" v={emp.bgv_status} />
          <KV k="Action" v={emp.bgv_action} pre />
        </Card>

        <Card title="Documents" hint={`Upload / replace · Appointment ${DOC_MAX_MB.appointment_letter} MB · Others ${DOC_MAX_MB.offer_letter} MB`}>
          <DocRow name="Internship letter" doc={emp.documents?.internship_letter} onDownload={() => download("internship_letter")} onUpload={(f) => uploadDoc("internship_letter", f)} onRemove={() => removeDoc("internship_letter")} testId="doc-intern" />
          <DocRow name="Offer letter" doc={emp.documents?.offer_letter} onDownload={() => download("offer_letter")} onUpload={(f) => uploadDoc("offer_letter", f)} onRemove={() => removeDoc("offer_letter")} testId="doc-offer" />
          <DocRow name="Appointment letter" doc={emp.documents?.appointment_letter} onDownload={() => download("appointment_letter")} onUpload={(f) => uploadDoc("appointment_letter", f)} onRemove={() => removeDoc("appointment_letter")} testId="doc-appt" />
        </Card>
      </div>

      <div className="mt-6 text-xs text-quiet font-mono">
        Onboarded on {emp.onboarding_completed_at ? new Date(emp.onboarding_completed_at).toLocaleString() : "—"} by {emp.onboarding_completed_by || "—"} · Last updated {emp.updated_at ? new Date(emp.updated_at).toLocaleString() : "—"}
      </div>
    </div>
  );
}

const Card = ({ title, hint, children }) => (
  <section className="bg-white border border-rule">
    <div className="px-5 py-3 border-b border-rule flex items-baseline justify-between">
      <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-quiet">{title}</div>
      {hint && <div className="font-mono text-[10px] text-quiet">{hint}</div>}
    </div>
    <div className="p-5 space-y-1.5">{children}</div>
  </section>
);

const KV = ({ k, v, mono, pre }) => (
  <div className="flex gap-3 items-baseline">
    <div className="font-mono text-[10px] uppercase tracking-wider text-quiet w-40 shrink-0">{k}</div>
    <div className={`text-sm text-ink flex-1 ${mono ? "font-mono" : ""} ${pre ? "whitespace-pre-wrap" : ""}`}>
      {v || "—"}
    </div>
  </div>
);

const DocRow = ({ name, doc, onDownload, onUpload, onRemove, testId }) => {
  const ref = useRef(null);
  const onPick = async (ev) => {
    const f = ev.target.files?.[0];
    if (!f) return;
    await onUpload(f);
    if (ref.current) ref.current.value = "";
  };
  return (
    <div className="border-b border-rule last:border-b-0 py-3 flex items-center justify-between gap-3">
      <div className="min-w-0">
        <div className="text-sm text-ink">{name}</div>
        <div className="text-xs text-quiet truncate">{doc?.filename || "not uploaded"}</div>
      </div>
      <div className="flex items-center gap-2 shrink-0">
        {doc?.filename && (
          <>
            <button
              data-testid={`${testId}-download`}
              onClick={onDownload}
              className="text-xs font-mono uppercase tracking-wider text-klein hover:underline"
            >
              Download ↓
            </button>
            <button
              data-testid={`${testId}-remove`}
              onClick={onRemove}
              className="text-xs font-mono uppercase tracking-wider text-error hover:underline"
            >
              Remove
            </button>
          </>
        )}
        <label
          className="text-xs font-mono uppercase tracking-wider text-white bg-klein hover:bg-klein-hover px-2 py-1 cursor-pointer"
          data-testid={`${testId}-upload-label`}
        >
          {doc?.filename ? "Replace" : "Upload"}
          <input
            ref={ref}
            data-testid={`${testId}-upload`}
            type="file"
            accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
            onChange={onPick}
            className="hidden"
          />
        </label>
      </div>
    </div>
  );
};
